import {
  Button,
  Chip,
  Divider,
  FormControl,
  FormLabel,
  Icons,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Typography,
} from '@healthinal/ui';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { getGetMedicationQueryOptions } from '../../../../../api/generated/medication-controller/medication-controller.ts';
import { MedicationDto, TherapyMedicationDto, TherapyMedicationDtoPhase } from '../../../../../api/generated/model';
import {
  getGetMedicationChangeHistoryQueryKey,
  getGetTherapyByPatientQueryKey,
  useUpdateTherapyPhaseMedicationByPatient,
} from '../../../../../api/generated/patient-controller/patient-controller.ts';
import { queryClient } from '../../../../../api/queryClient.ts';
import { SelectMedication } from '../SelectMedication.tsx';

export function AdjustMedicationDialog({
  open,
  onClose,
  currentTherapyMedication,
  phase,
  patientId,
}: {
  open: boolean;
  onClose: () => void;
  currentTherapyMedication: TherapyMedicationDto[];
  phase: number;
  patientId: string;
}) {
  const currentMedications = currentTherapyMedication.map((therapyMedication) => therapyMedication.medication);

  const { watch, control, setValue, handleSubmit, reset } = useForm<{ medication: MedicationDto[] }>({
    defaultValues: {
      medication: currentMedications,
    },
  });

  const selectedMedications = watch('medication');
  const deletedMedications = currentMedications.filter(
    (medication) => !selectedMedications.some((selectedMedication) => selectedMedication.name === medication.name),
  );
  const addedMedications = selectedMedications.filter(
    (selectedMedication) =>
      !currentMedications.some((currentMedication) => currentMedication.name === selectedMedication.name),
  );
  const isMedicationChanged = deletedMedications.length > 0 || addedMedications.length > 0;

  const updateMedicationMutation = useUpdateTherapyPhaseMedicationByPatient({
    mutation: {
      onSuccess: () => {
        void queryClient.resetQueries(getGetMedicationQueryOptions());
        void queryClient.invalidateQueries({
          queryKey: getGetTherapyByPatientQueryKey(patientId),
        });
        void queryClient.invalidateQueries({ queryKey: getGetMedicationChangeHistoryQueryKey(patientId) });
        onClose();
      },
      onError: () => toast.error('Bei dem Speichern der Medikation ist ein fehler aufgetreten'),
    },
  });

  const onSubmit = handleSubmit((data) => {
    updateMedicationMutation.mutate({
      id: patientId,
      phase: phase === 1 ? TherapyMedicationDtoPhase.PHASE_1 : TherapyMedicationDtoPhase.PHASE_2,
      data: data.medication,
    });
  });

  useEffect(() => {
    if (open) {
      reset({ medication: currentMedications });
    }
  }, [open, currentTherapyMedication]);

  const handleOnClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal open={open} onClose={handleOnClose}>
      <ModalDialog sx={{ width: 760 }}>
        <ModalClose />
        <Typography level={'h4'}>Medikation für Phase {phase} anpassen</Typography>
        <Divider />
        <form>
          <FormControl sx={{ gridColumn: '1 / span 2' }}>
            <FormLabel>
              <Typography>Medikamente</Typography>
            </FormLabel>
            <Controller
              name={'medication'}
              control={control}
              render={({ field: { onChange, value } }) => <SelectMedication value={value} onChange={onChange} />}
            />
          </FormControl>
        </form>
        {isMedicationChanged && (
          <>
            <Divider />
            <Typography>Änderungen</Typography>
            <Stack direction={'row'} gap={2}>
              <Stack width={'100%'} gap={1}>
                {deletedMedications.map((deletedMedication) => (
                  <Chip
                    key={deletedMedication.name}
                    onClick={() => setValue('medication', [...selectedMedications, deletedMedication])}
                    startDecorator={<Icons.RemoveCircleOutlineOutlined color={'danger'} />}>
                    {deletedMedication.name}
                  </Chip>
                ))}
              </Stack>
              <Divider orientation={'vertical'} />
              <Stack width={'100%'} gap={1}>
                {addedMedications.map((addedMedication) => (
                  <Chip
                    key={addedMedication.name}
                    onClick={() =>
                      setValue(
                        'medication',
                        selectedMedications.filter(
                          (selectedMedication) => selectedMedication.name !== addedMedication.name,
                        ),
                      )
                    }
                    startDecorator={<Icons.AddCircleOutline color={'success'} />}>
                    {addedMedication.name}
                  </Chip>
                ))}
              </Stack>
            </Stack>
          </>
        )}
        <Stack paddingTop={2} direction={'row'} justifyContent={'flex-end'} gap={1}>
          <Button color={'neutral'} onClick={handleOnClose}>
            Abbrechen
          </Button>
          <Button onClick={onSubmit} disabled={!isMedicationChanged} loading={updateMedicationMutation.isPending}>
            Speichern
          </Button>
        </Stack>
      </ModalDialog>
    </Modal>
  );
}
